import styles from './Issues.module.scss';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import { Button, Chip } from '@mui/material';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { IssueCauseIcon } from 'components/common/IssueCauseIcon/IssueCauseIcon';
import {
    convertDeliveryStatusToStatusColor,
    convertDeliveryStatusToDisplayName,
    ticketTriggerToDisplayName,
    defaultIssueCauseFilters,
} from 'lib/constants';
import { useAuth, useUserSession } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import { activateSnackbar } from 'store/features/ui';
import { useLazyGetIssueTicketsPageQuery } from 'store/services/issue-tickets';
import { Ticket, TicketDirectory } from 'types/User/firebaseTypes/ticket';
import { AddIssueModal } from './AddIssueModal/AddIssueModal';
import { Add } from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { IssueDetailsModal } from './IssueDetailsModal/IssueDetailsModal';
import { useAppDispatch } from 'store';
import { format } from 'date-fns';
import { useLazyGetDeviceByDeviceIdEsQuery } from 'store/services';
import { keyBy, uniq } from 'lodash';
import { DeviceLookup } from 'views/Planning/Planning';
import { FilterBar } from 'components/common/FilterBar/FilterBar';
import { filterByIssueCause } from 'helpers/issues';
import { UserTicketSession } from 'types/User/firebaseTypes';
import { updateUserSession } from 'lib/firebase';

export interface IssuesProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export const Issues = ({ viewAsSupplier }: IssuesProps) => {
    const initialRender = useRef(true);
    const session = useUserSession();
    const [allTickets, setAllTickets] = useState<Ticket[] | null>(null);
    const [filteredTickets, setFilteredTickets] = useState<Ticket[] | null>(
        null
    );
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const { user } = useAuth();

    const [getIssueTicketsPageQuery] = useLazyGetIssueTicketsPageQuery();
    const [causeFilters, setCauseFilters] = useState<Record<string, boolean>>(
        defaultIssueCauseFilters
    );

    const [getDevicesQuery, { isLoading: isDeviceListLoading }] =
        useLazyGetDeviceByDeviceIdEsQuery();

    const [didAddIssue, setDidAddIssue] = useState(false);
    const [didUpdateIssue, setDidUpdateIssue] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] =
        useState<boolean>(false);
    const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
    // devices reference list - because we aren't updating device objects within tickets
    const [devicesList, setDevicesList] = useState<DeviceLookup>({});
    const [isAddIssueModalOpen, setIsAddIssueModalOpen] = useState(false);
    const dispatch = useAppDispatch();
    const apiFetchInProgress = useRef(false);
    const componentUnmounted = useRef(false);

    const openDetailsModal = (params: GridRowParams) => {
        setActiveTicket(params.row);
        setIsDetailsModalOpen(true);
    };

    const resetVariables = () => {
        setTicketsLoading(false);
        setDidAddIssue(false);
        setDidUpdateIssue(false);
    };

    // Apply user session data to issue cause filters.
    useEffect(() => {
        if (session?.ticket && session.ticket.filters?.ticketType) {
            setCauseFilters(session.ticket.filters.ticketType);
        }
    }, [session?.ticket]);

    // Fetch tickets when the component mounts
    useEffect(() => {
        // Make sure to update this ref when the component mounts and unmounts
        componentUnmounted.current = false;

        // function to fetch all pages of tickets from napi endpoint via RTK Query
        const fetchAllIssuePaginated = async ({
            supplierId,
            ticketDirectory,
        }: {
            supplierId: string;
            ticketDirectory: TicketDirectory;
        }) => {
            // recursive function to fetch each page of tickets
            const fetchPage = (startAt?: string, limit?: number) => {
                return new Promise<void>((resolve, reject) => {
                    let apiPromise;

                    // if startAt is defined, then we're fetching a subsequent page
                    if (startAt) {
                        apiPromise = getIssueTicketsPageQuery({
                            supplierId,
                            ticketDirectory,
                            startAt,
                            limit,
                        });
                    } else {
                        // otherwise, we're fetching the first page
                        apiPromise = getIssueTicketsPageQuery({
                            supplierId,
                            ticketDirectory,
                            limit,
                        });
                    }

                    apiPromise
                        .then((result) => {
                            if (result?.isError && !limit) {
                                return fetchPage(startAt, 250);
                            }
                            let atLimit = false;
                            // If the component unmounted before this promise resolved, then we don't want to update state
                            if (componentUnmounted.current) {
                                resolve();
                                return;
                            }

                            const tickets = result?.data?.tickets;
                            const nextStartAt = result?.data?.nextStartAt;

                            // If tickets exist, then we can update the table data
                            if (tickets) {
                                // format tickets as an array
                                const ticketValues: Ticket[] =
                                    Object.values(tickets);

                                // update all tickets data (without overwriting whats already there)
                                setAllTickets((prevTickets) => {
                                    if (prevTickets) {
                                        const allTickets = [
                                            ...prevTickets,
                                            ...ticketValues,
                                        ];
                                        if (allTickets.length > 10000)
                                            atLimit = true;
                                        return allTickets;
                                    } else {
                                        return ticketValues;
                                    }
                                });

                                const deviceIds = uniq(
                                    ticketValues.map(
                                        (ticket) => ticket.deviceId
                                    )
                                );

                                getDevicesQuery({
                                    user,
                                    deviceId: deviceIds.join(' '),
                                    limit: deviceIds.length,
                                }).then((deviceResult) => {
                                    if (deviceResult?.data?.results) {
                                        setDevicesList((prevList) => ({
                                            ...prevList,
                                            ...keyBy(
                                                deviceResult?.data?.results,
                                                (device) => device.deviceId
                                            ),
                                        }));
                                    }
                                });
                            }

                            // recursively fetch more data if there was a nextStartAt key returned and we haven't retrieved "too many" tickets
                            if (nextStartAt && !atLimit) {
                                fetchPage(nextStartAt, limit)
                                    .then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        reject(error);
                                    });
                                // if no nextStartAt key, then we're done fetching
                            } else {
                                resolve();
                            }

                            // Throw error if api call fails
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            };

            // initiate recursive function and return promise
            return fetchPage();
        };

        // function to fetch tickets and handle component states asynchronously
        const fetchTickets = async () => {
            // If an API call is already in progress, return early
            if (apiFetchInProgress.current) {
                return;
            }

            try {
                // if valid supplier id, fetch tickets
                const supplierId =
                    viewAsSupplier ?? user?.protected?.supplierId;
                if (user && (supplierId || supplierId?.toString() === '0')) {
                    // set flag that we are starting a api fetch (prevent multiple fetches when useEffect is triggered multiple times)
                    apiFetchInProgress.current = true;

                    // set loading state for the table while we load first page of tickets
                    setTicketsLoading(true);

                    // make sure we reset all tickets data to before we start fetching
                    setAllTickets(null);

                    // fetch active issue tickets
                    await fetchAllIssuePaginated({
                        supplierId,
                        ticketDirectory: 'active',
                    });

                    // fetch resolved issue tickets
                    // await fetchAllIssuePaginated({
                    //     supplierId,
                    //     ticketDirectory: 'resolved',
                    // });
                }
            } catch (error) {
                // Show snackbar with error message when fetch fails
                dispatch(
                    activateSnackbar({
                        message: JSON.stringify(error),
                        severity: 'error',
                    })
                );
            }

            // After we finish fetching, reset flags
            apiFetchInProgress.current = false;

            // reset variables
            resetVariables();
        };

        // initiate ticket fetch async
        fetchTickets();

        // return a callback that sets flag to true when component unmounts
        // this will cause any ongoing API calls to abort
        return () => {
            componentUnmounted.current = true;
        };
    }, [
        user,
        dispatch,
        viewAsSupplier,
        didAddIssue,
        didUpdateIssue,
        getIssueTicketsPageQuery,
        getDevicesQuery,
    ]);

    // Apply filters to issue tickets.
    useEffect(() => {
        if (allTickets) {
            setFilteredTickets(filterByIssueCause(causeFilters, allTickets));
        }
    }, [allTickets, causeFilters]);

    // Make network call to update the issue ticket session filters.
    useEffect(() => {
        if (user?.uid) {
            if (initialRender.current) {
                initialRender.current = false;
            } else {
                // Default as if there is no existing session data.
                let newSessionEntry: UserTicketSession = {
                    filters: { ticketType: causeFilters },
                };

                // Now check if there is existing session data and copy everything that isn't changing.
                if (session?.ticket) {
                    // Copy existing session since we are not replacing all session fields.
                    newSessionEntry = {
                        ...session.ticket,
                    };

                    // If filters exist on the ticket session, update the ticket type filters with causeFilters.
                    if (session.ticket?.filters) {
                        newSessionEntry.filters = {
                            // Copy other existing filters.
                            ...session.ticket.filters,
                            ticketType: causeFilters,
                        };
                    }
                }

                updateUserSession(user.uid, 'ticket', newSessionEntry);
            }
        }
    }, [causeFilters, session, session?.ticket, user?.uid]);

    const issueTicketColumns: DataTableProps['columns'] = [
        {
            headerName: 'Date Created',
            field: 'dateCreated_iso',
            fbKey: 'dateCreated_iso',
            valueFormatter: (param) => {
                if (param.value)
                    return format(new Date(param.value), 'yyyy/MM/dd');
            },
        },
        {
            headerName: 'Status',
            field: 'status',
            fbKey: 'status',
            valueFormatter: (param) => {
                return convertDeliveryStatusToDisplayName[param.value];
            },
            renderCell: (params) => {
                // Issue tickets and delivery tickets current have the same set of statuses. If those ever diverge, we will need
                // To create separate `convertDeliveryStatusToStatusColor` and `convertDeliveryStatusToDisplayName` data.
                return (
                    <Chip
                        sx={{
                            backgroundColor:
                                convertDeliveryStatusToStatusColor[
                                    params.value
                                ],
                            color: 'black',
                        }}
                        label={convertDeliveryStatusToDisplayName[params.value]}
                    />
                );
            },
        },
        {
            headerName: 'Ticket Name',
            field: 'name',
            fbKey: 'name',
        },
        {
            headerName: 'Cause',
            field: 'ticketTrigger',
            fbKey: 'ticketTrigger',
            valueFormatter: (param) => ticketTriggerToDisplayName[param.value],
            renderCell: (params) => {
                return <IssueCauseIcon trigger={params.value} />;
            },
        },
        {
            headerName: 'Assigned User',
            field: 'taggedUsers',
            fbKey: 'taggedUsers',
            valueFormatter: (param) => {
                if (param.value && param.value[0]?.email)
                    return param.value[0]?.email;
                return 'Unassigned';
            },
            renderCell: (params) => {
                if (params?.value && params.value[0]?.email)
                    return <Chip label={params.value[0]?.email} />;
                else return <Chip color="warning" label="Unassigned" />;
            },
        },
        {
            headerName: 'Customer Name',
            field: 'device.customer_name',
            fbKey: 'customerName',
            valueGetter: (param) => {
                return devicesList[param.row?.deviceId]?.customer_name ?? '';
            },
        },
        {
            headerName: 'Tank Name',
            field: 'device.name',
            fbKey: 'name',
            valueGetter: (param) => {
                return devicesList[param.row?.deviceId]?.name ?? '';
            },
        },
        {
            headerName: 'Short Device Id',
            field: 'device.shortDeviceId',
            fbKey: 'shortDeviceId',
            valueGetter: (param) => {
                return devicesList[param.row?.deviceId]?.shortDeviceId ?? '';
            },
        },
        {
            headerName: 'Address',
            field: 'device.address',
            fbKey: 'address',
            valueGetter: (param) => {
                return devicesList[param.row?.deviceId]?.address ?? '';
            },
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            fbKey: 'createdBy',
            renderCell: (params) => {
                return (
                    <Chip
                        label={
                            <CreatedBy ticket={params.row} showIcon={false} />
                        }
                    />
                );
            },
        },
    ];

    return (
        <div data-testid="Issues" className={styles.container}>
            <FilterBar
                filterGroups={[
                    {
                        name: 'Issue Cause',
                        filters: causeFilters,
                        onUpdateFilters: setCauseFilters,
                    },
                ]}
            />
            <DataTable
                id="issues"
                data={filteredTickets}
                columns={issueTicketColumns}
                loading={ticketsLoading || isDeviceListLoading}
                dataFormatter={(data) => data}
                csvOptions={{
                    fileName: 'issue-tickets',
                }}
                onRowClick={openDetailsModal}
                getRowCount={(data) => data?.length ?? 0}
                toolbarComponents={
                    <Button
                        onClick={() => setIsAddIssueModalOpen(true)}
                        variant="text"
                        startIcon={<Add />}
                    >
                        Add Issue
                    </Button>
                }
            />
            {activeTicket && (
                <IssueDetailsModal
                    modalOpen={isDetailsModalOpen}
                    setModalOpen={setIsDetailsModalOpen}
                    ticket={activeTicket}
                    setActiveTicket={setActiveTicket}
                    onIssueUpdated={setDidUpdateIssue}
                />
            )}
            <AddIssueModal
                modalOpen={isAddIssueModalOpen}
                setModalOpen={setIsAddIssueModalOpen}
                onAddIssue={setDidAddIssue}
                devicesList={devicesList}
                setDevicesList={setDevicesList}
                supplierId={viewAsSupplier ?? user?.protected?.supplierId ?? ''}
            />
        </div>
    );
};
